import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import Flex from '@react-css/flex';
import Heading from 'storybookConfig/stories/molecules/Heading';
import SignUpPageLayout from 'storybookConfig/stories/species/Layout/SignUpPageLayout';
import Stepper, { Step } from 'storybookConfig/stories/organisms/Stepper';
import Body from 'storybookConfig/stories/molecules/Body';
import ContactDescription from './ContactDescription';
import ContactForm from './ContactForm';
import BrandInfoDescription from './BrandInfoDescription';
import BrandInfoForm from './BrandInfoForm';
import SocialsForm from './BrandSocialsForm';
import BrandIdentityForm from './BrandIdentityForm';
import BrandAssetsForm from './BrandAssetsForm';
import BrandAssetsDescription from './BrandAssetsDescription';
import AdditionalInfoForm from './AdditionalInfoForm';
import AdditionalInfoDescription from './AdditionalInfoDescription';
import FinishedDescription from './FinishedDescription';
import { useAppSelector } from 'store/index';
import { selectCompany } from 'store/selectors/me/company';
import useSegment from 'hooks/useSegment';
import type { MarketplaceFormData } from './types';

const MpOnboardingPageLayout = SignUpPageLayout;

const RIGHT_PANEL_WIDTH = '450px';

const LeftSide = styled(MpOnboardingPageLayout.Content)`
  flex: 1;
  padding: 32px 48px;
  margin-right: ${RIGHT_PANEL_WIDTH};

  @media (max-width: 700px) {
    display: none;
    margin-right: 0;
  }
`;

const RightSide = styled(MpOnboardingPageLayout.Aside)`
  position: fixed;
  min-width: ${RIGHT_PANEL_WIDTH};
  max-width: ${RIGHT_PANEL_WIDTH};
  padding: 40px;

  @media (max-width: 700px) {
    min-width: 100%;
    max-width: 100%;
    padding: 20px;
    border-radius: 0;
  }
`;

const stepOrder = [
  'contact',
  'socials',
  'brand-info',
  'values',
  'assets',
  'additional-info',
] as const;

type StepKey = (typeof stepOrder)[number];

// Map of steps to Segment event names
const stepEventMap: Record<StepKey | 'finished', string> = {
  contact: 'Supplier Onboarding Contact Info Viewed',
  socials: 'Supplier Onboarding Social Profiles Viewed',
  'brand-info': 'Supplier Onboarding Brand Info Viewed',
  values: 'Supplier Onboarding Brand Attributes Viewed',
  assets: 'Supplier Onboarding Brand Assets Viewed',
  'additional-info': 'Supplier Onboarding Additional Info Viewed',
  finished: 'Supplier Onboarding Completed',
};

const MarketplaceOnboardingPage = () => {
  const company = useAppSelector(selectCompany);
  const { step } = useParams<{ step: string }>();
  const analytics = useSegment();

  // Step state
  const [isFinished, setIsFinished] = useState(false);
  const currentStep = (step as StepKey) || 'contact';
  // Form state
  const [marketplaceData, setMarketplaceData] = useState<MarketplaceFormData | undefined>();
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (step === 'finished') {
      setIsFinished(true);
    }
  }, [step]);

  useEffect(() => {
    if (!analytics || !step) return;

    const stepKey = step as StepKey | 'finished';
    const eventName = stepEventMap[stepKey];

    if (!eventName) return;

    const sessionKey = `onboarding_step_tracked_${stepKey}`;
    const hasTracked = sessionStorage.getItem(sessionKey);

    if (!hasTracked) {
      analytics.track(eventName, {});
      sessionStorage.setItem(sessionKey, 'true');
    }
  }, [analytics, step]);

  useEffect(() => {
    if (company && !marketplaceData) {
      setMarketplaceData(company.marketplaceSettings);
    }
  }, [company, marketplaceData]);

  const goToNextStep = () => {
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      const nextStep = stepOrder[currentIndex + 1];
      navigate(`/marketplace/onboarding/${nextStep}`);
      analytics?.track('Supplier Onboarding Step Back Clicked', {
        step_name: nextStep,
      });
    } else {
      navigate('/marketplace/onboarding/finished');
      analytics?.track('Supplier Onboarding Step Back Clicked', {
        step_name: 'finished',
      });
    }
  };

  const goToPreviousStep = () => {
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      const previousStep = stepOrder[currentIndex - 1];
      analytics?.track('Supplier Onboarding Step Back Clicked', {
        step_name: previousStep,
      });
      navigate(`/marketplace/onboarding/${previousStep}`);
    }
  };

  const handleMarketplaceDataChange = (values: Partial<MarketplaceFormData>) => {
    setMarketplaceData((prev) => ({
      ...prev,
      ...values,
    }));
  };

  const steps = [
    {
      key: 'contact',
      label: 'Step 1',
      description: 'Contact Information',
      leftContent: <ContactDescription />,
      rightContent: (
        <ContactForm
          onNext={goToNextStep}
          marketplaceData={marketplaceData}
          setMarketplaceData={handleMarketplaceDataChange}
        />
      ),
    },
    {
      key: 'socials',
      label: 'Step 2.1',
      description: 'Social Profiles',
      leftContent: <BrandInfoDescription marketplaceData={marketplaceData} />,
      rightContent: (
        <SocialsForm
          onNext={goToNextStep}
          onBack={goToPreviousStep}
          marketplaceData={marketplaceData}
          setMarketplaceData={handleMarketplaceDataChange}
        />
      ),
    },
    {
      key: 'brand-info',
      label: 'Step 2.2',
      description: 'Tell Us About Your Brand',
      leftContent: <BrandInfoDescription marketplaceData={marketplaceData} />,
      rightContent: (
        <BrandInfoForm
          onNext={goToNextStep}
          onBack={goToPreviousStep}
          marketplaceData={marketplaceData}
          setMarketplaceData={handleMarketplaceDataChange}
        />
      ),
    },
    {
      key: 'values',
      label: 'Step 2.3',
      description: 'Brand Identity & Attributes',
      leftContent: <BrandInfoDescription marketplaceData={marketplaceData} />,
      rightContent: (
        <BrandIdentityForm
          onNext={goToNextStep}
          onBack={goToPreviousStep}
          marketplaceData={marketplaceData}
          setMarketplaceData={handleMarketplaceDataChange}
        />
      ),
    },
    {
      key: 'assets',
      label: 'Step 3',
      description: 'Showcase Your Brand',
      leftContent: <BrandAssetsDescription marketplaceData={marketplaceData} />,
      rightContent: (
        <BrandAssetsForm
          onNext={goToNextStep}
          onBack={goToPreviousStep}
          marketplaceData={marketplaceData}
          setMarketplaceData={handleMarketplaceDataChange}
        />
      ),
    },
    {
      key: 'additional-info',
      label: 'Step 4',
      description: 'Additional Information',
      leftContent: (
        <AdditionalInfoDescription
          marketplaceData={marketplaceData}
          focusedField={focusedField}
          setMarketplaceData={handleMarketplaceDataChange}
        />
      ),
      rightContent: (
        <AdditionalInfoForm
          onNext={goToNextStep}
          onBack={goToPreviousStep}
          marketplaceData={marketplaceData}
          setMarketplaceData={handleMarketplaceDataChange}
          setFocusedField={setFocusedField}
        />
      ),
    },
  ];

  const currentStepIndex = stepOrder.indexOf(currentStep);
  const currentStepData = steps.find((s) => s.key === currentStep);

  if (isFinished) {
    return (
      <MpOnboardingPageLayout.Root hasOnboardingBackground hasBlueBackground={false}>
        <FinishedDescription />
      </MpOnboardingPageLayout.Root>
    );
  }

  if (!currentStepData) {
    return null;
  }

  const getStepStatus = (index: number, current: number): 'active' | 'completed' | 'default' => {
    if (index === current) return 'active';
    if (index < current) return 'completed';
    return 'default';
  };

  return (
    <MpOnboardingPageLayout.Root hasOnboardingBackground hasBlueBackground={false}>
      <LeftSide>
        <Flex column gap="24px">
          <Stepper>
            {steps.map((stepperStep, index) => (
              <Step key={stepperStep.key} status={getStepStatus(index, currentStepIndex)}>
                <Body variant="Body/Body Small Bold" color="white">
                  {stepperStep.label}
                </Body>
                <br />
                <Body variant="Body/Body Small" color="white">
                  {stepperStep.description}
                </Body>
              </Step>
            ))}
          </Stepper>
          {currentStepData.leftContent}
        </Flex>
      </LeftSide>
      <RightSide>
        <Flex column gap="32px">
          {currentStepIndex === 0 ? (
            <Heading variant="Headings/H2">Join the Marketplace</Heading>
          ) : null}
          {currentStepData.rightContent}
        </Flex>
      </RightSide>
    </MpOnboardingPageLayout.Root>
  );
};

export default MarketplaceOnboardingPage;
